import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { useLocationId } from '@/data/useLocationId';
import { settingsApi } from '../api/settingsApi';
import type { SettingsAppointmentReason } from '../api/types';

function reasonsQueryKey(locationId: number, includeInactive: boolean) {
  return [...keys.settings.reasons(locationId), includeInactive] as const;
}

export function useAppointmentReasons(includeInactive = false) {
  const locationId = useLocationId();
  return useQuery({
    queryKey: reasonsQueryKey(locationId ?? 0, includeInactive),
    queryFn: ({ signal }) =>
      settingsApi.getAppointmentReasons(locationId!, includeInactive, { signal }),
    enabled: locationId != null,
    staleTime: 300_000,
  });
}

export function useCreateAppointmentReason() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SettingsAppointmentReason>) => {
      if (locationId == null) throw new Error('Location required');
      return settingsApi.createAppointmentReason(locationId, data);
    },
    onSuccess: () => {
      if (locationId != null) {
        void queryClient.invalidateQueries({ queryKey: keys.settings.reasons(locationId) });
      }
    },
  });
}

export function useUpdateAppointmentReason() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SettingsAppointmentReason> }) => {
      if (locationId == null) throw new Error('Location required');
      return settingsApi.updateAppointmentReason(locationId, id, data);
    },
    onSuccess: () => {
      if (locationId != null) {
        void queryClient.invalidateQueries({ queryKey: keys.settings.reasons(locationId) });
      }
    },
  });
}

export function useDeleteAppointmentReason() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (locationId == null) throw new Error('Location required');
      return settingsApi.deleteAppointmentReason(locationId, id);
    },
    onSuccess: () => {
      if (locationId != null) {
        void queryClient.invalidateQueries({ queryKey: keys.settings.reasons(locationId) });
      }
    },
  });
}
